/**
 * voice-v8 — Rückfragen mit Ja-Knopf: EINE Prüfung für die Schritte-Weiche (frontend/app.ts `renderToolCard`) und den Kartenbau
 * (lib/tool-cards.ts). (22.10.2026)
 *
 * Ein Ergebnis, das auf Martins Ja wartet, wird sonst eine leise Zeile „wartet auf dein Ja“ in den Schritten — die Karte mit dem Knopf
 * entsteht nie. So fehlte der Knopf beim Trainingsplan (Martin 11:37: „es gibt nicht mal den Ja-Punkt“) und danach bei den Shorts
 * (Martin 15:08: „Da ist kein Button zum Anklicken!“). Eine neue Knopf-Rückfrage wird HIER eingetragen — dann gilt sie für beide Wege.
 *
 * Nur ein gültiger Vorschlag (Kennung nach VORSCHLAG_KENNUNG) wird Karte; alles andere bleibt, wie es war.
 */
import { istShortsSchritt } from "../../../shared/shorts.js";
import { VORSCHLAG_KENNUNG } from "../../../shared/training.js";
import { unwrapResult } from "./hilfe.js";
import { fristAus, meldeNichtEingeloest } from "./knopf-paar.js";
import { buildMandatCard, buildProzessFreigabeCard, istMandatRueckfrage, istProzessRueckfrage } from "./prozess.js";
import { buildShortsVorschlagCard, istShortsRueckfrage } from "./shorts.js";
import { MANDAT_KARTE, PROZESS_KARTE } from "../../../shared/prozess-freigabe.js";
import { buildTrainingVorschlagCard, istTrainingRueckfrage } from "./training.js";
import { buildZugangswerteCard, istZugangswerteRueckfrage } from "./zugangswerte.js";
import { ZUGANGSWERTE_KARTE } from "../../../shared/zugangswert.js";
import { buildKonfigCard, istKonfigRueckfrage } from "./konfig-ablage.js";
import { KONFIG_KARTE } from "../../../shared/konfig-ablage.js";

const RUECKFRAGEN: ReadonlyArray<{
  werkzeug: (tool: string) => boolean;
  ist: (tool: string, result: unknown) => boolean;
  baue: (result: unknown) => HTMLElement | null;
}> = [
  { werkzeug: (t) => t === "training_plan_aendern", ist: istTrainingRueckfrage, baue: buildTrainingVorschlagCard },
  { werkzeug: (t) => t.startsWith("shorts_") && istShortsSchritt(t.slice("shorts_".length)), ist: istShortsRueckfrage, baue: buildShortsVorschlagCard },
  // 27.10.2026: die Prozess-Freigabe (server/prozess-freigabe.ts) — kein Werkzeug, ein Ergebnis des Servers an der Grenze eines Browser-Prozesses.
  { werkzeug: (t) => t === PROZESS_KARTE, ist: istProzessRueckfrage, baue: buildProzessFreigabeCard },
  // 27.10.2026: die Mandats-Karte (server/prozess-freigabe.ts `mandatAnfragen`) — vom Server gefüllt, drei Stufen-Knöpfe.
  { werkzeug: (t) => t === MANDAT_KARTE, ist: istMandatRueckfrage, baue: buildMandatCard },
  // 27.10.2026 (B9): die Gruppen-Karte der Zugangswerte (server/zugangswert-ablage.ts) — Namen und Herkunft, EIN Knopf.
  { werkzeug: (t) => t === ZUGANGSWERTE_KARTE, ist: istZugangswerteRueckfrage, baue: buildZugangswerteCard },
  // 28.10.2026 (B10b): die Karte „Postausgang schreiben“ (server/konfig-ablage.ts) — feste Variablen, Namen aus der Ablage, EIN Knopf.
  { werkzeug: (t) => t === KONFIG_KARTE, ist: istKonfigRueckfrage, baue: buildKonfigCard },
];

/** Ist dieses Ergebnis eine offene Rückfrage mit Ja-Knopf? Dann wird es Karte, nie Schritt-Zeile. */
export function istKnopfRueckfrage(tool: string, result: unknown): boolean {
  return RUECKFRAGEN.some((r) => r.ist(tool, result));
}

/** Die Karte der Rückfrage — `null`, wenn das Ergebnis keine Knopf-Rückfrage ist. */
export function buildKnopfRueckfrageCard(tool: string, result: unknown): HTMLElement | null {
  return RUECKFRAGEN.find((r) => r.ist(tool, result))?.baue(result) ?? null;
}

/** Gründe, aus denen der Vorschlag NICHT eingelöst wurde und ein neuer Tipp auf denselben Knopf ihn noch einlösen kann. */
const NEU_TIPPEN = new Set(["bestaetigung-fremdtext", "bestaetigung-noetig"]);

/**
 * 22.10.2026 (Martin 16:42: Ja getippt, Ja-Tor lehnte wegen Fremdtext im Zug ab, Knopf blieb grau): ein Ergebnis eines Knopf-Werkzeugs
 * kam an (live oder aus dem Verlauf). Meldet es „nicht eingelöst“ für einen noch gültigen Vorschlag (Kennung im Ergebnis), wird der
 * Knopf der Karte mit DIESER Kennung wieder frei — nur, wenn ihr Ja gesendet war (karten/knopf-paar.ts). Ein eingelöstes Ergebnis
 * (`ok: true`) und jeder andere Grund ändern nichts: die Karte bleibt aus. ⚠️ Nur Anzeige — das Ja-Tor am Server bleibt unberührt.
 */
export function meldeKnopfStand(tool: string, result: unknown): void {
  if (!RUECKFRAGEN.some((r) => r.werkzeug(tool))) return;
  const r = unwrapResult(result);
  if (r.ok === true || r.ausgefuehrt === true) return;
  const grund = typeof r.code === "string" ? r.code : r.error;
  if (typeof grund !== "string" || !NEU_TIPPEN.has(grund)) return;
  if (typeof r.vorschlag_id !== "string" || !VORSCHLAG_KENNUNG.test(r.vorschlag_id)) return;
  meldeNichtEingeloest(r.vorschlag_id, fristAus(r.gueltig_bis_ms));
}
